import React, { FC } from 'react'
import { SxProps, Theme } from '@mui/material'
import { Flex, Text, Box } from '@fundamentals'
import {
  Card,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@common/material'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { localizedStrings } from '@core/strings'

type LayerItemProps = {
  layer: ProjectMapLayer
  // Small tag shown after the layer name saying what kind of layer this is
  // (e.g. the source file type, or that it's a browser-local tileset).
  chip?: { label: string; tooltip?: string }
  onEdit?: (layer: ProjectMapLayer) => void
  onShowHide?: (layer: ProjectMapLayer) => void
  onDelete?: (layer: ProjectMapLayer) => void
  onZoomTo?: (layer: ProjectMapLayer) => void
  onDownload?: (layer: ProjectMapLayer) => void
  isLoading?: boolean
  sx?: SxProps<Theme>
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}

export const LayerItem: FC<LayerItemProps> = ({
  layer,
  chip,
  onEdit,
  onShowHide,
  onDelete,
  onZoomTo,
  onDownload,
  isLoading = false,
  sx,
  dragHandleProps,
}) => {
  return (
    <Card
      sx={{
        p: 3,
        mb: 2,
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Flex alignItems='center' justifyContent='space-between'>
        <Flex alignItems='center' gap={1}>
          {dragHandleProps && (
            <Box
              {...dragHandleProps}
              sx={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            >
              <DragIndicatorIcon sx={{ opacity: 0.5 }} />
            </Box>
          )}
          <Text variant='subtitle1'>{layer.name}</Text>
          {chip &&
            (chip.tooltip ? (
              <Tooltip title={chip.tooltip}>
                <Chip label={chip.label} size='small' variant='outlined' />
              </Tooltip>
            ) : (
              <Chip label={chip.label} size='small' variant='outlined' />
            ))}
        </Flex>

        <Flex alignItems='center'>
          {onEdit && (
            <IconButton
              onClick={(event) => {
                onEdit(layer)
              }}
              sx={{ ml: 1 }}
            >
              <Tooltip title={localizedStrings.editLayerName}>
                <EditOutlinedIcon />
              </Tooltip>
            </IconButton>
          )}

          {onShowHide && (
            <IconButton
              onClick={(event) => {
                onShowHide(layer)
              }}
              sx={{ ml: 1 }}
            >
              <Tooltip title={localizedStrings.showHideLayer}>
                {layer.visible ? (
                  <VisibilityOutlinedIcon />
                ) : (
                  <VisibilityOffOutlinedIcon />
                )}
              </Tooltip>
            </IconButton>
          )}

          {onZoomTo && (
            <IconButton
              onClick={(event) => {
                onZoomTo(layer)
              }}
              sx={{ ml: 1 }}
            >
              <Tooltip title={localizedStrings.zoomToLayer}>
                <MyLocationOutlinedIcon />
              </Tooltip>
            </IconButton>
          )}

          {onDelete && (
            <IconButton
              onClick={(event) => {
                onDelete(layer)
              }}
              sx={{ ml: 1 }}
            >
              <Tooltip title={localizedStrings.deleteLayer}>
                <DeleteOutlineOutlinedIcon color='error' />
              </Tooltip>
            </IconButton>
          )}

          {onDownload && (
            <IconButton
              onClick={(event) => {
                onDownload(layer)
              }}
              sx={{ ml: 1 }}
            >
              <Tooltip title={localizedStrings.downloadLayerFile}>
                <FileDownloadOutlinedIcon />
              </Tooltip>
            </IconButton>
          )}
        </Flex>
      </Flex>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <LinearProgress />
        </Box>
      )}
    </Card>
  )
}
